import { coerceClockDate, dualClockStamp } from "./clock";
import type { DataSourceKind } from "./dataSource";
import type { FlightLogEntry, Mission } from "./types";

/**
 * Actual time of departure on the canonical ticket.
 *
 * Prefer SimConnect / tracker wheels-off, then out / off-blocks.
 * User edit beats sim. Do not invent — missing stays empty.
 */
export const TICKET_ATD_SOURCES = ["user_override", "simconnect"] as const;

export type TicketAtdSource = (typeof TICKET_ATD_SOURCES)[number];

export type TicketAtdStamp = {
  actualDepartureUtc: string;
  actualDepartureSource: TicketAtdSource;
};

export type TicketAtdFields = {
  actualDepartureUtc?: string | null;
  actualDepartureSource?: string | null;
};

export type SimAtdTimes = {
  airborneUtc?: string | null;
  offBlocksUtc?: string | null;
};

export type PunctualityKind = "early" | "on_time" | "late";

export const PUNCTUALITY_KIND_LABEL: Record<PunctualityKind, string> = {
  early: "Early",
  on_time: "On time",
  late: "Late",
};

export function isTicketAtdSource(value: unknown): value is TicketAtdSource {
  return value === "user_override" || value === "simconnect";
}

export function hydrateTicketAtdSource(raw?: unknown): TicketAtdSource | undefined {
  return isTicketAtdSource(raw) ? raw : undefined;
}

export function parseAtdIso(iso?: string | null): string | null {
  const date = coerceClockDate(iso);
  return date ? date.toISOString() : null;
}

function isUserAtd(ticket?: TicketAtdFields | null): boolean {
  return Boolean(parseAtdIso(ticket?.actualDepartureUtc) && ticket?.actualDepartureSource === "user_override");
}

/**
 * User edit > SimConnect wheels-off > SimConnect out. Never invents a time.
 */
export function resolveTicketAtd(input: {
  ticket?: TicketAtdFields | null;
  telemetry?: SimAtdTimes | null;
}): TicketAtdStamp | null {
  const ticketIso = parseAtdIso(input.ticket?.actualDepartureUtc);
  if (ticketIso && input.ticket?.actualDepartureSource === "user_override") {
    return { actualDepartureUtc: ticketIso, actualDepartureSource: "user_override" };
  }
  const airborne = parseAtdIso(input.telemetry?.airborneUtc);
  if (airborne) return { actualDepartureUtc: airborne, actualDepartureSource: "simconnect" };
  const out = parseAtdIso(input.telemetry?.offBlocksUtc);
  if (out) return { actualDepartureUtc: out, actualDepartureSource: "simconnect" };
  if (ticketIso) {
    return {
      actualDepartureUtc: ticketIso,
      actualDepartureSource: hydrateTicketAtdSource(input.ticket?.actualDepartureSource) ?? "simconnect",
    };
  }
  return null;
}

export function ticketAtdIso(
  ticket?: TicketAtdFields | null,
  telemetry?: SimAtdTimes | null,
): string | null {
  return resolveTicketAtd({ ticket, telemetry })?.actualDepartureUtc ?? null;
}

/**
 * Write sim ATD onto a ticket. User override is sticky. Wheels-off replaces
 * an earlier out stamp. Same ISO is a no-op.
 */
export function stampSimAtd(ticket: TicketAtdFields | null | undefined, sim: SimAtdTimes | null | undefined): TicketAtdStamp | null {
  if (isUserAtd(ticket)) return null;
  const airborne = parseAtdIso(sim?.airborneUtc);
  const out = parseAtdIso(sim?.offBlocksUtc);
  const next = airborne ?? (parseAtdIso(ticket?.actualDepartureUtc) ? null : out);
  if (!next) return null;
  if (parseAtdIso(ticket?.actualDepartureUtc) === next) return null;
  return { actualDepartureUtc: next, actualDepartureSource: "simconnect" };
}

export function applyUserAtd(iso: string | null | undefined): Partial<Mission> {
  const parsed = parseAtdIso(iso);
  if (!parsed) {
    return { actualDepartureUtc: undefined, actualDepartureSource: undefined };
  }
  return { actualDepartureUtc: parsed, actualDepartureSource: "user_override" };
}

/** Local user ATD beats remote; otherwise first known stamp wins. */
export function mergeTicketAtd(local: TicketAtdFields, remote: TicketAtdFields): TicketAtdStamp | Record<string, never> {
  const left = resolveTicketAtd({ ticket: local });
  const right = resolveTicketAtd({ ticket: remote });
  if (left?.actualDepartureSource === "user_override") return left;
  if (right?.actualDepartureSource === "user_override") return right;
  if (left) return left;
  if (right) return right;
  return {};
}

export function applyAtdStamp<T extends TicketAtdFields>(ticket: T, stamp: TicketAtdStamp | null): T {
  if (!stamp) return ticket;
  return {
    ...ticket,
    actualDepartureUtc: stamp.actualDepartureUtc,
    actualDepartureSource: stamp.actualDepartureSource,
  };
}

export function projectLogAtd(log: FlightLogEntry, atdIso: string | null): FlightLogEntry {
  if (!atdIso) return { ...log, actualDepartureUtc: undefined };
  const stamp = dualClockStamp(new Date(atdIso));
  return {
    ...log,
    actualDepartureUtc: atdIso,
    departedAtUtc: atdIso,
    departedAtLocal: stamp.local,
  };
}

/** ATD − STD in whole minutes. Null when either clock is missing. */
export function punctualityDeltaMin(stdIso?: string | null, atdIso?: string | null): number | null {
  const std = coerceClockDate(stdIso);
  const atd = coerceClockDate(atdIso);
  if (!std || !atd) return null;
  return Math.round((atd.getTime() - std.getTime()) / 60_000);
}

export function punctualityKind(deltaMin: number | null): PunctualityKind | null {
  if (deltaMin == null || !Number.isFinite(deltaMin)) return null;
  if (deltaMin < 0) return "early";
  if (deltaMin > 0) return "late";
  return "on_time";
}

export function formatPunctualityDelta(deltaMin: number | null): string {
  const kind = punctualityKind(deltaMin);
  if (kind == null || deltaMin == null) return "—";
  if (kind === "on_time") return "on time";
  const abs = Math.abs(deltaMin);
  const unit = abs === 1 ? "min" : "min";
  return kind === "early" ? `${abs} ${unit} early` : `${abs} ${unit} late`;
}

export function atdSourceToDataKind(source?: TicketAtdSource | null): DataSourceKind | null {
  if (source === "user_override") return "user_override";
  if (source === "simconnect") return "simconnect";
  return null;
}

/** Stamp ATD onto matching live tickets from a SimConnect session. No-op when unchanged. */
export function stampMissionsAtdFromTelemetry<T extends TicketAtdFields & { id: string }>(
  missions: readonly T[],
  session: (SimAtdTimes & { tripId?: string | null }) | null | undefined,
): T[] | null {
  if (!session?.tripId) return null;
  let changed = false;
  const next = missions.map((mission) => {
    if (mission.id !== session.tripId) return mission;
    const stamp = stampSimAtd(mission, session);
    if (!stamp) return mission;
    changed = true;
    return applyAtdStamp(mission, stamp);
  });
  return changed ? next : null;
}
